import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ChartFilters } from '../../models/chart-filters.model';
import {IDataSets, IMwgData, Itable} from '../../models/chart-mwg.model';
import { inverseMatrix } from '../../shared/helpers/inverse-matrix';
import {VectorService} from "../../services/vector/vector.service";
import {MatDialog} from "@angular/material/dialog";
import {OffsetHistoryService} from "../../services/vector/offset-history.service";
import {IBatchForecastMeasurement} from "../../models/batch-forecast-measurement.model";
import {OffsetDto} from "../../models/vector-api/offset-dto.model";
import {VectorHistoryService} from "../../services/vector/vector-history.service";
import {TranslateModule} from "@ngx-translate/core";
import {DatePipe, DecimalPipe, NgClass} from "@angular/common";
import {KpiLabelPipe} from "../../shared/pipes/kpi-label.pipe";
import {LabelChartType} from "../../shared/pipes/label-chart-type.pipe";
import {MatIcon} from "@angular/material/icon";
import {MatTooltip} from "@angular/material/tooltip";

/**
 * Represents a table list view for body and bumper values. Used in the MWG.
 */
@Component({
  selector: 'colimo-chart-table',
  templateUrl: 'chart-table.component.html',
  styleUrls: ['chart-table.component.scss'],
  imports: [
    TranslateModule,
    DatePipe,
    KpiLabelPipe,
    NgClass,
    DecimalPipe,
    LabelChartType,
    MatIcon,
    MatTooltip
  ]
})
export class ChartTableComponent implements OnChanges {
  @Input() public dataSet: IDataSets;
  @Input() public table: Itable;
  @Input() public angles: string[];
  @Input() public selectedAngles: boolean[];
  @Input() public tolerance: boolean;
  @Input() public batchType: 'BODY' | 'BUMPER';
  @Input() public filters: ChartFilters;
  @Input() public colorIndicator: string;
  @Input() public labTintingMode: string;
  @Input() public tintingTheoryAvg: IMwgData;
  @Input() public tintingOffsetAvg: IMwgData;
  @Input() public selectedOffsetBatch: any;
  @Input() public selectedOffsetMeasurement: IBatchForecastMeasurement;
  @Input() public selectedCarbBatch: any;
  @Input() public selectedCarbAvg: IMwgData;
  @Input() public selectedTintingTarget: string;

  public tableData: number[][];
  public tableColors: string[][];
  public tableHighlights: boolean[][];
  public correspondingTableData: number[][];
  public correspondingTableColors: string[][];
  public correspondingTableHighlights: boolean[][];
  public typeLabels: string[];

  constructor(private readonly vectorService: VectorService,
              private readonly offsetHistoryService: OffsetHistoryService,
              private offsetDialog: MatDialog) {
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.updateTableData();
  }

  /**
   * Returns the data for each color value (dl, da, db, ...)
   */
  private getColorValueData(dataSet: IDataSets, index: number): number[][] {
    return Object.keys(dataSet).map((key) => {
      return dataSet[key].dataSets[index].data;
    });
  }

  private getColorValueColors(dataSet: IDataSets, index: number): string[][] {
    return Object.keys(dataSet).map((key) => {
      return dataSet[key].dataSets[index].colors;
    });
  }

  private getColorValueHighlights(dataSet: IDataSets, index: number): boolean[][] {
    return Object.keys(dataSet).map((key) => {
      return dataSet[key].dataSets[index].highlights;
    });
  }

  public async showOffsetAvgClicked(): Promise<void> {
    const { OffsetDialogComponent } = await import(
      '../../pages/vector-tinting/offset-dialog/offset-dialog.component'
      );
    if (!this.tintingOffsetAvg || !this.tintingTheoryAvg || !this.selectedOffsetBatch) {
      return;
    }
    const shiftedOffsetFromHistory = this.offsetHistoryService.getFromHistory(this.selectedOffsetBatch, this.selectedOffsetMeasurement, this.filters, this.tintingTheoryAvg, this.selectedTintingTarget);
    // check if the requested mwg data was already calculated and display it in dialog when present
    if (shiftedOffsetFromHistory && shiftedOffsetFromHistory.AvgView) {

      this.offsetDialog.open(OffsetDialogComponent, {
        data: {
          offsetDialogTitle: 'Color theory offset-shifted-target',
          mwgData: shiftedOffsetFromHistory.AvgView,
        },
      });
      return;
    }

    // call webservice to get subtracted avg view with correct colors according to tolerances
    this.vectorService.subtractOffsetAvg(this.filters.batchId, this.tintingTheoryAvg, this.tintingOffsetAvg).subscribe({
      next: (response: IMwgData) => {
        if (response) {
          // save to storage
          const offsetShifted: OffsetDto = { AvgView: response };
          this.offsetHistoryService.addToHistory(this.selectedOffsetBatch, this.selectedOffsetMeasurement, this.filters, offsetShifted, this.tintingTheoryAvg, this.selectedTintingTarget);

          // show offset dialog with subtracted offset
          this.offsetDialog.open(OffsetDialogComponent, {
            data: {
              offsetDialogTitle: 'Color theory offset-shifted-target',
              mwgData: response,
            },
          });
        }
      }, error: (error) => {
        // do nothing
      }
    })
  }

  public async showCarbAvgClicked(): Promise<void> {
    const { OffsetDialogComponent } = await import(
      '../../pages/vector-tinting/offset-dialog/offset-dialog.component'
      );
    if (!this.selectedCarbAvg || !this.tintingTheoryAvg || !this.selectedCarbBatch) {
      return;
    }

    const shiftedCarbFromHistory = this.offsetHistoryService.getFromHistory(this.selectedCarbBatch, null, this.filters, this.tintingTheoryAvg, this.selectedTintingTarget);
    // check if the requested mwg data was already calculated and display it in dialog when present
    if (shiftedCarbFromHistory && shiftedCarbFromHistory.AvgView) {
      this.offsetDialog.open(OffsetDialogComponent, {
        data: {
          offsetDialogTitle: 'Color theory carb-shifted-target',
          mwgData: shiftedCarbFromHistory.AvgView,
        },
      });
      return;
    }

    this.vectorService.subtractOffsetAvg(this.filters.batchId, this.tintingTheoryAvg, this.selectedCarbAvg).subscribe({
      next: (response: IMwgData) => {
        if (response) {
          // save to storage
          const carbShifted: OffsetDto = { AvgView: response };
          this.offsetHistoryService.addToHistory(this.selectedCarbBatch, null, this.filters, carbShifted, this.tintingTheoryAvg, this.selectedTintingTarget);
          // show offset dialog with subtracted offset
          this.offsetDialog.open(OffsetDialogComponent, {
            data: {
              offsetDialogTitle: 'Color theory carb-shifted-target',
              mwgData: response,
            },
          });
        }
      }, error: (error) => {
        // do nothing
      }
    })
  }

  /**
   * Updates the transposition matrix for body and bumper.
   * Updates also the labels.
   */
  private updateTableData(): void {
    // Update Labels
    this.typeLabels = Object.keys(this.dataSet);

    // Update table data
    if (this.batchType === 'BODY') {
      this.tableData = inverseMatrix(this.getColorValueData(this.dataSet, 1));
      this.tableColors = inverseMatrix(this.getColorValueColors(this.dataSet, 1));
      this.tableHighlights = inverseMatrix(this.getColorValueHighlights(this.dataSet, 1));
      this.correspondingTableData = inverseMatrix(this.getColorValueData(this.dataSet, 0));
      this.correspondingTableColors = inverseMatrix(this.getColorValueColors(this.dataSet, 0));
      this.correspondingTableHighlights = inverseMatrix(this.getColorValueHighlights(this.dataSet, 0));
    }
    if (this.batchType === 'BUMPER') {
      this.tableData = inverseMatrix(this.getColorValueData(this.dataSet, 0));
      this.tableColors = inverseMatrix(this.getColorValueColors(this.dataSet, 0));
      this.tableHighlights = inverseMatrix(this.getColorValueHighlights(this.dataSet, 0));
      this.correspondingTableData = inverseMatrix(this.getColorValueData(this.dataSet, 1));
      this.correspondingTableColors = inverseMatrix(this.getColorValueColors(this.dataSet, 1));
      this.correspondingTableHighlights = inverseMatrix(this.getColorValueHighlights(this.dataSet, 1));
    }
  }

  protected readonly VectorHistoryService = VectorHistoryService;
}
